import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseResizableOptions {
  initialWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  onWidthChange?: (width: number) => void;
}

export interface UseResizableReturn {
  width: number;
  isResizing: boolean;
  handleMouseDown: (e: React.MouseEvent) => void;
  hasDragged: boolean;
  didDrag: () => boolean; // ref-based, accurate at click time
}

/**
 * Reusable hook for drag-to-resize functionality
 * Can be used for any resizable panel/element
 */
export function useResizable({
  initialWidth = 256,
  minWidth = 180,
  maxWidth = 480,
  onWidthChange
}: UseResizableOptions = {}): UseResizableReturn {
  const [width, setWidth] = useState(initialWidth);
  const [isResizing, setIsResizing] = useState(false);
  const dragStartXRef = useRef<number | null>(null);
  const dragThreshold = 5; // pixels
  const dragDeltaRef = useRef(0); // persist beyond mouseup until next mousedown

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    dragStartXRef.current = e.clientX;
    dragDeltaRef.current = 0;
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;

      if (dragStartXRef.current !== null) {
        dragDeltaRef.current = Math.abs(e.clientX - dragStartXRef.current);
      }

      const newWidth = Math.max(minWidth, Math.min(maxWidth, e.clientX));
      setWidth(newWidth);
      onWidthChange?.(newWidth);
    },
    [isResizing, minWidth, maxWidth, onWidthChange]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Check if user actually dragged (moved > threshold)
  const hasDragged = dragDeltaRef.current > dragThreshold;

  const didDrag = () => dragDeltaRef.current > dragThreshold;

  return {
    width,
    isResizing,
    handleMouseDown,
    hasDragged,
    didDrag
  };
}
